import {
  Award,
  Briefcase,
  Calendar,
  CalendarCheck,
  GraduationCap,
  Languages,
  Layers,
  MapPin,
  Sparkles,
  User,
  UserCheck,
} from 'lucide-react';
import { ROLE_LABEL } from '@/constants/roles';
import { EMPLOYMENT_TYPE_LABEL } from '@/features/institute/staff/constants';
import { formatDate } from '@/lib/format';
import type { OwnStaffProfile } from '@/types/staff';
import { ProfileField } from './ProfileField';

export interface TeacherProfessionalDetailsTabProps {
  profile: OwnStaffProfile;
}

/**
 * Employment and background, as the school office recorded them.
 *
 * The record keeps one qualification line, years of experience, languages and
 * skills — so that is what is shown. The tables of degrees, past schools and
 * certificates this tab used to draw had nothing behind them.
 */
export function TeacherProfessionalDetailsTab({ profile }: TeacherProfessionalDetailsTabProps) {
  const details = profile.details;
  const experience =
    details?.experienceYears != null
      ? `${details.experienceYears} ${details.experienceYears === 1 ? 'year' : 'years'}`
      : null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="card px-4 py-3 space-y-6">
        <div className="flex items-center gap-3 border-b border-line pb-4">
          <Briefcase className="w-6 h-6 text-indigo-600 dark:text-indigo-400" aria-hidden />
          <h3 className="text-lg font-black text-ink">Employment Details</h3>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <ProfileField
            icon={<span className="text-xs font-black font-mono">ID</span>}
            tone="bg-emerald-500/10 border-emerald-500/20 text-emerald-600 dark:text-emerald-400"
            label="Employee ID"
            value={profile.employeeCode}
            mono
          />
          <ProfileField
            icon={<User className="w-5 h-5" />}
            tone="bg-amber-500/10 border-amber-500/20 text-amber-600 dark:text-amber-400"
            label="Designation"
            value={profile.designation}
          />
          <ProfileField
            icon={<Layers className="w-5 h-5" />}
            tone="bg-blue-500/10 border-blue-500/20 text-blue-600 dark:text-blue-400"
            label="Role"
            value={profile.roleName ?? ROLE_LABEL[profile.staffType]}
          />
          <ProfileField
            icon={<Calendar className="w-5 h-5" />}
            tone="bg-cyan-500/10 border-cyan-500/20 text-cyan-600 dark:text-cyan-400"
            label="Date of Joining"
            value={profile.joinedOn ? formatDate(profile.joinedOn) : null}
          />
          <ProfileField
            icon={<CalendarCheck className="w-5 h-5" />}
            tone="bg-teal-500/10 border-teal-500/20 text-teal-600 dark:text-teal-400"
            label="Confirmed On"
            value={details?.confirmedOn ? formatDate(details.confirmedOn) : null}
          />
          <ProfileField
            icon={<Briefcase className="w-5 h-5" />}
            tone="bg-violet-500/10 border-violet-500/20 text-violet-600 dark:text-violet-400"
            label="Employment Type"
            value={details?.employmentType ? EMPLOYMENT_TYPE_LABEL[details.employmentType] : null}
          />
          <ProfileField
            icon={<MapPin className="w-5 h-5" />}
            tone="bg-rose-500/10 border-rose-500/20 text-rose-600 dark:text-rose-400"
            label="Work Location"
            value={details?.workLocation}
          />
          <ProfileField
            icon={<UserCheck className="w-5 h-5" />}
            tone="bg-indigo-500/10 border-indigo-500/20 text-indigo-600 dark:text-indigo-400"
            label="Reporting To"
            value={details?.reportingTo}
          />
        </div>
      </div>

      <div className="card px-4 py-3 space-y-6">
        <div className="flex items-center gap-3 border-b border-line pb-4">
          <GraduationCap className="w-6 h-6 text-indigo-600 dark:text-indigo-400" aria-hidden />
          <h3 className="text-lg font-black text-ink">Qualifications & Experience</h3>
        </div>

        <div className="space-y-5">
          <ProfileField
            icon={<GraduationCap className="w-5 h-5" />}
            tone="bg-purple-500/10 border-purple-500/20 text-purple-600 dark:text-purple-400"
            label="Qualification"
            value={details?.qualification}
          />
          <ProfileField
            icon={<Award className="w-5 h-5" />}
            tone="bg-amber-500/10 border-amber-500/20 text-amber-600 dark:text-amber-400"
            label="Experience"
            value={experience}
          />
          <ProfileField
            icon={<Languages className="w-5 h-5" />}
            tone="bg-sky-500/10 border-sky-500/20 text-sky-600 dark:text-sky-400"
            label="Languages Known"
            value={details?.languagesKnown}
          />
          <ProfileField
            icon={<Sparkles className="w-5 h-5" />}
            tone="bg-pink-500/10 border-pink-500/20 text-pink-600 dark:text-pink-400"
            label="Skills & Interests"
            value={details?.skills}
          />
        </div>
      </div>
    </div>
  );
}
